import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Stack, SimpleGrid, Card, Title, Text, Group, Button } from '@repletoryreach/sdk-ui';
import { PageHeader, Alert } from '@repletoryreach/sdk-ui/data-display';
import { SegmentedControl } from '@repletoryreach/sdk-ui/inputs';
import { AreaChart, DonutChart } from '@repletoryreach/sdk-ui/charts';
import { Link } from 'react-router-dom';
import { admin, channelWa } from '@repletoryreach/sdk-api';

import { StatCard } from '../../components/StatCard';
import { useBillingStats, useInboxStats } from '../../data/useStats';
import { useTenants } from '../../data/useTenants';
import { useUsers } from '../../data/useUsers';
import { formatInteger, formatMinor, formatRelative, formatDate } from '../../lib/format';
import { navIcon } from '../../app/shell/nav-icons';
import { ActivityTimeline } from '../../app/components/ActivityTimeline';
import { HealthGrid } from '../../app/components/HealthGrid';
import { useT } from '../../i18n/I18nProvider';
import './dashboard.css';

/** The ranges the toolbar offers, in days. */
const RANGES = [7, 14, 30, 90] as const;
type RangeDays = (typeof RANGES)[number];

/** What `listLedger` is asked for. The endpoint takes a row cap, not a date. */
const LEDGER_LIMIT = 1000;

/** The users endpoint caps its page at 500 rows, with no meta to say so. */
const USERS_PAGE_CAP = 500;

/** How many days back a period-on-period delta needs before it can be drawn. */
function startOf(daysAgo: number): number {
  return Date.now() - daysAgo * 86_400_000;
}

function dayKey(d: Date): string {
  return d.toISOString().slice(0, 10);
}
function dayLabel(d: Date): string {
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** Rows created inside the window, for the "new in the last N days" deltas. */
function createdSince(rows: Array<{ created_at?: string | null }>, since: number): number {
  return rows.filter((r) => {
    const t = r.created_at ? new Date(r.created_at).getTime() : NaN;
    return !Number.isNaN(t) && t >= since;
  }).length;
}

export default function DashboardPage() {
  const t = useT();
  const [rangeDays, setRangeDays] = useState<RangeDays>(7);
  const billing = useBillingStats();
  const inbox = useInboxStats();
  const tenants = useTenants();
  const users = useUsers();
  const ledger = useQuery({
    queryKey: ['admin', 'ledger', 'dashboard'],
    queryFn: () => admin.listLedger({ limit: LEDGER_LIMIT })
  });
  const numbers = useQuery({
    queryKey: ['admin', 'numbers', 'dashboard'],
    queryFn: () => admin.listPhoneNumbers()
  });

  const tenantRows = tenants.data?.data ?? [];
  const activeTenants = tenantRows.filter((row) => row.is_active).length;
  const userRows = users.data?.data ?? [];
  const userCount = userRows.length;
  const usersCapped = userCount >= USERS_PAGE_CAP;
  const conversations = inbox.data?.data.conversations ?? 0;
  const messages = inbox.data?.data.messages ?? 0;
  const ledger7d = billing.data?.data.ledger_volume_7d ?? 0;
  const wallets = billing.data?.data.wallets ?? [];

  // A dashboard that fails closed to zero is worse than one that fails loudly:
  // "R 0 billed" reads as a quiet week rather than a dead billing service. Each
  // feed that errored is named here, and every card it fed shows a dash.
  const feeds = [
    { query: billing, label: t('dashboard.feeds.billing') },
    { query: inbox, label: t('dashboard.feeds.inbox') },
    { query: tenants, label: t('dashboard.feeds.tenants') },
    { query: users, label: t('dashboard.feeds.users') },
    { query: ledger, label: t('dashboard.feeds.ledger') },
    { query: numbers, label: t('dashboard.feeds.numbers') }
  ];
  const failed = feeds.filter((f) => f.query.isError);
  // What Meta has registered and verified (3 October 2026): a line needing a person is said here, not only on its page.
  const metaStatus = useQuery({ queryKey: ['admin', 'meta-status'], queryFn: () => channelWa.metaStatus(), staleTime: 300_000, retry: false, refetchOnWindowFocus: false });
  const metaAttention = metaStatus.data?.data.attention ?? 0;
  const noValue = t('dashboard.noValue');

  const lastUpdated = Math.max(...feeds.map((f) => f.query.dataUpdatedAt || 0));
  const refreshing = feeds.some((f) => f.query.isFetching);

  function refreshAll() {
    for (const feed of feeds) void feed.query.refetch();
  }

  // Daily billed volume derived from the most recent ledger entries
  // (sku wa.* = one entry per billed message; llm.* = one per token direction),
  // plus the ZAR billed inside the chosen window and the window before it.
  const { messageSeries, messageSparkline, billedZarMinor, billedDelta } = useMemo(() => {
    const entries = ledger.data?.data ?? [];
    const buckets = new Map<string, { messages: number; llm: number }>();
    const labels = new Map<string, string>();
    for (let i = rangeDays - 1; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      buckets.set(dayKey(d), { messages: 0, llm: 0 });
      labels.set(dayKey(d), dayLabel(d));
    }

    const windowStart = startOf(rangeDays);
    const previousStart = startOf(rangeDays * 2);
    let zar = 0;
    let previousZar = 0;
    for (const e of entries) {
      if (!e.sku_code) continue;
      const at = e.occurred_at ? new Date(e.occurred_at).getTime() : NaN;
      if (e.currency === 'ZAR' && !Number.isNaN(at)) {
        if (at >= windowStart) zar += Math.abs(e.amount_minor);
        else if (at >= previousStart) previousZar += Math.abs(e.amount_minor);
      }
      const key = e.occurred_at?.slice(0, 10);
      const bucket = key ? buckets.get(key) : undefined;
      if (!bucket) continue;
      if (e.sku_code.startsWith('wa.')) bucket.messages += 1;
      else if (e.sku_code.startsWith('llm.')) bucket.llm += 1;
    }
    const series = [...buckets.entries()].map(([key, v]) => ({
      date: labels.get(key) ?? key,
      messages: v.messages,
      llm: v.llm
    }));

    // The ledger endpoint takes a row cap and no `since`, so a full page means
    // the earlier window is truncated and any comparison against it would
    // understate the past. The delta is only drawn when the page is short
    // enough to hold both windows in full.
    const truncated = entries.length >= LEDGER_LIMIT;
    const change = !truncated && previousZar > 0
      ? ((zar - previousZar) / previousZar) * 100
      : null;

    return {
      messageSeries: series,
      messageSparkline: series.map((p) => p.messages),
      billedZarMinor: zar,
      billedDelta: change
    };
  }, [ledger.data, rangeDays]);

  // Real channel health from cross-tenant phone-number quality ratings.
  const channelHealth = useMemo(() => {
    const nums = numbers.data?.data ?? [];
    const counts = { healthy: 0, warning: 0, down: 0, unrated: 0 };
    for (const n of nums) {
      if (!n.is_active) { counts.down += 1; continue; }
      const q = (n.quality_rating ?? '').toUpperCase();
      if (q === 'GREEN' || q === 'HIGH') counts.healthy += 1;
      else if (q === 'YELLOW' || q === 'MEDIUM') counts.warning += 1;
      else if (q === 'RED' || q === 'LOW') counts.down += 1;
      else counts.unrated += 1;
    }
    return {
      total: nums.length,
      healthy: counts.healthy,
      data: [
        { name: t('status.healthy'), value: counts.healthy, color: 'var(--rr-color-green-500)' },
        { name: t('status.warning'), value: counts.warning, color: 'var(--rr-color-yellow-500)' },
        { name: t('status.down'),    value: counts.down,    color: 'var(--rr-color-red-500)' },
        { name: t('gateways.numbers.kpi.unrated'), value: counts.unrated, color: 'var(--rr-color-gray-500)' }
      ].filter((s) => s.value > 0)
    };
  }, [numbers.data, t]);

  const healthyCount = channelHealth.healthy;
  const windowStart = startOf(rangeDays);
  const newTenants = createdSince(tenantRows, windowStart);
  const newUsers = createdSince(userRows, windowStart);
  const deltaCaption = t('dashboard.delta.newInDays', { days: rangeDays });
  const rangeWindow = t('dashboard.range.window', {
    from: formatDate(new Date(windowStart).toISOString()),
    to: formatDate(new Date().toISOString())
  });

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('settings.layout.nav.platform')}
        title={t('dashboard.welcome')}
        description={t('dashboard.description')}
        actions={
          <Link to="/audit" style={{ color: 'var(--rr-color-primary)', fontSize: 13 }}>{t('dashboard.openAuditLog')}</Link>
        }
      />

      {/* One window drives the whole page: the chart buckets, the billed-usage
          figure and the "new in the last N days" deltas. The page used to mix
          14 days for the chart with "the last 1,000 entries" for money. */}
      <Group justify="space-between" align="center" wrap="wrap">
        <Group gap="sm" align="center">
          {lastUpdated > 0 && (
            <Text fz="xs" c="var(--rr-color-text-muted)">
              {t('dashboard.updated', { when: formatRelative(new Date(lastUpdated).toISOString()) })}
            </Text>
          )}
          <Button size="xs" variant="default" onClick={refreshAll} loading={refreshing}>
            {t('action.refresh')}
          </Button>
        </Group>
        <Group gap="sm" align="center">
          <SegmentedControl
            size="xs"
            value={String(rangeDays)}
            onChange={(v) => setRangeDays(Number(v) as RangeDays)}
            data={RANGES.map((days) => ({ value: String(days), label: t('dashboard.range.days', { days }) }))}
          />
          <Text fz="xs" c="var(--rr-color-text-muted)">{rangeWindow}</Text>
        </Group>
      </Group>

      {metaAttention > 0 && (
        <Alert color="warning" title={t('dashboard.meta.title')} data-testid="dashboard-meta-alert">
          <Group justify="space-between" wrap="wrap">
            <Text fz="sm">{t('dashboard.meta.body', { count: metaAttention })}</Text>
            <Link to="/gateways/meta-status">{t('dashboard.meta.action')}</Link>
          </Group>
        </Alert>
      )}

      {failed.length > 0 && (
        <Alert color="error" className="admin-dash__feeds" title={
          failed.length === 1 ? t('dashboard.error.countOne') : t('dashboard.error.count', { count: failed.length })
        }>
          <Stack gap="sm" align="flex-start">
            <Text fz="sm">{failed.map((f) => f.label).join(', ')}</Text>
            <details>
              <summary>{t('dashboard.error.details')}</summary>
              <Stack gap="sm" align="flex-start" style={{ marginTop: 10 }}>
                <Text fz="sm">{t('dashboard.error.body', { feeds: failed.map((f) => f.label).join(', ') })}</Text>
                <Button size="xs" variant="outline" onClick={refreshAll} loading={refreshing}>
                  {t('action.retry')}
                </Button>
              </Stack>
            </details>
          </Stack>
        </Alert>
      )}

      {/* Fixed column counts never went responsive, so a phone rendered four
          KPI cards side by side at roughly 80px each. */}
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
        <StatCard
          label={t('dashboard.metrics.tenants')}
          value={tenants.isError ? noValue : formatInteger(activeTenants)}
          loading={tenants.isLoading}
          icon={navIcon('/tenants')}
          tone="success"
          // Organisations carry `created_at`, so "new in the window" is a real
          // count rather than a period-on-period guess at a figure nobody
          // stores. Where there is no such history the card shows a hint.
          delta={newTenants > 0 ? { value: formatInteger(newTenants), direction: 'up', good: true, caption: deltaCaption } : undefined}
          hint={newTenants > 0 ? undefined : t('dashboard.metrics.tenantsHintActive', { total: formatInteger(tenantRows.length) })}
          to="/tenants"
          linkLabel={t('dashboard.links.tenants')}
        />
        <StatCard
          label={t('users.title')}
          value={users.isError ? noValue : formatInteger(userCount)}
          loading={users.isLoading}
          icon={navIcon('/users')}
          tone="info"
          delta={!usersCapped && newUsers > 0 ? { value: formatInteger(newUsers), direction: 'up', good: true, caption: deltaCaption } : undefined}
          hint={usersCapped ? t('dashboard.metrics.usersCapped') : newUsers > 0 ? undefined : t('dashboard.metrics.usersHint')}
          to="/users"
          linkLabel={t('dashboard.links.users')}
        />
        <StatCard
          label={t('dashboard.metrics.messages')}
          value={inbox.isError ? noValue : formatInteger(messages)}
          loading={inbox.isLoading}
          icon={navIcon('/conversations')}
          tone="primary"
          sparkline={messageSparkline}
          hint={t('dashboard.metrics.messagesHint', { count: formatInteger(conversations) })}
          to="/conversations"
          linkLabel={t('dashboard.links.conversations')}
        />
        <StatCard
          label={t('gateways.numbers.title')}
          value={numbers.isError ? noValue : formatInteger(channelHealth.total)}
          loading={numbers.isLoading}
          icon={navIcon('/gateways/numbers')}
          tone="warning"
          hint={t('dashboard.metrics.numbersHint', { count: formatInteger(healthyCount) })}
          to="/gateways/numbers"
          linkLabel={t('dashboard.links.numbers')}
        />
      </SimpleGrid>

      {/* The delivery-rate card that used to sit here was a hardcoded 98.7%
          with a hardcoded +0.3 trend, wearing the same chrome as the two real
          metrics beside it. Nothing computes it yet, so it is gone. */}
      <SimpleGrid cols={{ base: 1, md: 3 }} spacing="md">
        <StatCard
          label={t('dashboard.metrics.billedUsageRange', { days: rangeDays })}
          value={ledger.isError ? noValue : formatMinor(billedZarMinor, 'ZAR')}
          loading={ledger.isLoading}
          icon={navIcon('/billing/usage')}
          tone="success"
          delta={billedDelta !== null ? {
            value: `${Math.abs(billedDelta).toFixed(1)}%`,
            direction: billedDelta >= 0 ? 'up' : 'down',
            good: billedDelta >= 0,
            caption: t('dashboard.delta.previousDays', { days: rangeDays })
          } : undefined}
          hint={billedDelta !== null ? undefined : t('dashboard.metrics.billedUsageHint')}
          to="/billing/ledger"
          linkLabel={t('dashboard.links.ledger')}
        />
        <StatCard
          label={t('billing.overview.kpi.ledgerVolume')}
          value={billing.isError ? noValue : formatInteger(ledger7d)}
          loading={billing.isLoading}
          icon={navIcon('/billing/ledger')}
          tone="warning"
          hint={t('billing.overview.kpi.entries')}
          to="/billing/ledger"
          linkLabel={t('dashboard.links.ledger')}
        />

        {/* `billingStats` has always returned per-currency wallet totals and the
            page threw them away. Platform float by currency is the one number
            here that belongs to the platform rather than to a tenant. */}
        <Card withBorder>
          <Stack gap="md">
            <Group justify="space-between" align="baseline">
              <Text role="overline" c="var(--rr-color-text-muted)">{t('dashboard.wallets.title')}</Text>
              <Text fz="xs" c="var(--rr-color-text-muted)">
                {wallets.length > 0
                  ? t('dashboard.wallets.currencies', { count: formatInteger(wallets.length) })
                  : t('dashboard.wallets.hint')}
              </Text>
            </Group>
            {wallets.length === 0 ? (
              <Text fz="sm" c="var(--rr-color-text-muted)">
                {billing.isError ? noValue : billing.isLoading ? t('common.loading') : t('dashboard.wallets.empty')}
              </Text>
            ) : (
              <div className="admin-dash__wallets">
                {wallets.map((w) => (
                  <div key={w.currency} className="admin-dash__wallet">
                    {/* i18n-ignore: ISO 4217 currency code */}
                    <Text fz="xs" fw={600} c="var(--rr-color-primary)">{w.currency}</Text>
                    <Text fz="sm" fw={700}>{formatMinor(w.balance_minor_total, w.currency)}</Text>
                    <Text fz="xs" c="var(--rr-color-text-muted)">
                      {t('dashboard.wallets.count', { count: formatInteger(w.wallet_count) })}
                    </Text>
                  </div>
                ))}
              </div>
            )}
            <Link to="/billing" style={{ color: 'var(--rr-color-primary)', fontSize: 12.5, fontWeight: 600, marginTop: 'auto' }}>
              {t('dashboard.wallets.link')} <span aria-hidden>→</span>
            </Link>
          </Stack>
        </Card>
      </SimpleGrid>

      <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
        <Card withBorder>
          <Stack gap="md">
            <Group justify="space-between" align="baseline">
              <Title order={4}>{t('dashboard.billedActivity')}</Title>
              <Text fz="xs" c="var(--rr-color-text-muted)">{t('dashboard.billedActivityHint', { days: rangeDays })}</Text>
            </Group>
            <AreaChart
              h={220}
              data={messageSeries}
              dataKey="date"
              series={[
                { name: 'messages', label: t('dashboard.metrics.messages') },
                { name: 'llm',      label: t('dashboard.series.llmEvents') }
              ]}
              withGradient
              withTooltip
              withLegend
              curveType="monotone"
            />
            <Group justify="flex-end">
              <Link to="/billing/usage" style={{ color: 'var(--rr-color-primary)', fontSize: 12.5, fontWeight: 600 }}>
                {t('dashboard.links.usage')} <span aria-hidden>→</span>
              </Link>
            </Group>
          </Stack>
        </Card>
        <Card withBorder>
          <Stack gap="md">
            <Group justify="space-between" align="baseline">
              <Title order={4}>{t('dashboard.channelHealth')}</Title>
              <Text fz="xs" c="var(--rr-color-text-muted)">{t('dashboard.channelHealthHint')}</Text>
            </Group>
            <div className="admin-dash__donut">
              <div className="admin-dash__donut-chart">
                <DonutChart
                  h={200}
                  size={200}
                  data={channelHealth.data.length > 0 ? channelHealth.data : [
                    { name: t('dashboard.noNumbers'), value: 1, color: 'var(--rr-color-gray-600)' }
                  ]}
                  innerRadiusRatio={0.62}
                  centerLabel={
                    <Stack gap={0} align="center">
                      <Text fz="xl" fw={700}>{formatInteger(channelHealth.total)}</Text>
                      <Text fz="xxs" c="var(--rr-color-text-muted)">{t('dashboard.numbersLabel')}</Text>
                    </Stack>
                  }
                  withTooltip
                  withLegend={false}
                />
              </div>
              {/* The legend carries the count and the share per state, which is
                  what makes the donut answer "how many are unhealthy". */}
              <div className="admin-dash__legend">
                {channelHealth.data.length === 0 ? (
                  <Text fz="sm" c="var(--rr-color-text-muted)">
                    {numbers.isError ? noValue : numbers.isLoading ? t('common.loading') : t('dashboard.noNumbers')}
                  </Text>
                ) : channelHealth.data.map((slice) => (
                  <div key={slice.name} className="admin-dash__legend-row">
                    <span className="admin-dash__legend-dot" style={{ background: slice.color }} aria-hidden />
                    <Text fz="sm">{slice.name}</Text>
                    <Text fz="sm" fw={600} className="admin-dash__legend-value">
                      {t('dashboard.legend.value', {
                        count: formatInteger(slice.value),
                        share: ((slice.value / channelHealth.total) * 100).toFixed(1)
                      })}
                    </Text>
                  </div>
                ))}
              </div>
            </div>
            <Group justify="flex-end">
              <Link to="/gateways/numbers" style={{ color: 'var(--rr-color-primary)', fontSize: 12.5, fontWeight: 600 }}>
                {t('dashboard.links.numbers')} <span aria-hidden>→</span>
              </Link>
            </Group>
          </Stack>
        </Card>
      </SimpleGrid>

      <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
        <ActivityTimeline />
        <HealthGrid />
      </SimpleGrid>
    </Stack>
  );
}
